<template>
    <div class="tw:w-full" :class="props.customClass">
        <ValidationProvider :name="props.name" :rules="computedRules" :value="localValue" v-slot="{ errors }" mode="eager">
            <div class="tw:relative tw:w-full" :data-validation-error="errors[0] ? '' : undefined">
                <div v-if="props.loading" class="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:z-20">
                    <svg class="tw:animate-spin tw:h-5 tw:w-5 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                </div>
                <input
                    v-model="localValue"
                    :data-cy="props.dataCy"
                    :type="props.type"
                    :name="props.name || undefined"
                    :placeholder="props.placeholder || ' '"
                    :maxlength="props.maxlength"
                    :disabled="props.disabled || props.loading"
                    class="tw:block tw:p-2 tw:w-full tw:text-sm tw:rounded tw:border tw:z-8 tw:appearance-none tw:focus:outline-none tw:peer"
                    :class="[
                        errors[0]
                            ? 'tw:border-red-500 tw:bg-red-50/50 tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400 tw:focus:bg-white'
                            : 'tw:border-slate-300 tw:bg-white tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400',
                        (props.maxlength || props.loading) && props.icon
                            ? 'tw:pr-16' : props.maxlength || props.loading
                                ? 'tw:pr-10' : props.icon
                                    ? 'tw:pr-10'
                                    : ''
                    ]"
                    v-bind="$attrs"
                    @input="handleInput"
                    @change="handleChange"
                />
                <span
                    v-if="props.label"
                    class="tw:absolute tw:uppercase tw:duration-300 tw:z-8 tw:pointer-events-none tw:tracking-wide tw:origin-left tw:bg-white tw:px-2
                    tw:start-1 tw:top-2 tw:-translate-y-4 tw:scale-75
                    tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:-translate-y-1/2 tw:peer-placeholder-shown:top-1/2 tw:peer-placeholder-shown:font-normal
                    tw:peer-focus:top-2 tw:peer-focus:-translate-y-4 tw:peer-focus:scale-75 tw:peer-focus:px-2"
                    :class="[
                        errors[0]
                            ? 'tw:text-red-400 tw:peer-placeholder-shown:bg-transparent tw:peer-focus:bg-white tw:peer-focus:text-blue-800 tw:peer-focus:font-bold'
                            : 'tw:text-slate-600 tw:font-bold tw:peer-focus:text-blue-800 tw:peer-focus:font-bold'
                    ]"
                >
                    {{ props.label }}
                </span>
                <i
                    v-if="props.icon"
                    :class="props.icon"
                    class="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-slate-400 tw:pointer-events-none"
                ></i>
                <span
                    v-if="props.maxlength"
                    class="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:text-xs tw:text-slate-400"
                    :class="props.icon ? 'tw:right-8' : ''"
                >
                    {{ localValue.length }}/{{ props.maxlength }}
                </span>
            </div>

            <span
                v-if="errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formatErrorMessage(errors[0]) }}
            </span>

            <span
                v-if="props.apiValidationError && !errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';

    const props = defineProps({
        label: String,
        name: String,
        rules: [String, Object],
        value: [String, Number],
        type: { type: String, default: 'text' },
        disabled: Boolean,
        loading: Boolean,
        maxlength: [String, Number],
        apiValidationError: [String, Array],
        icon: String,
        customClass: String,
        dataCy: String,
        placeholder: String
    });

    const emit = defineEmits(['input', 'change']);

    const computedRules = computed(() => props.rules || '');

    const localValue = ref(props.value != null ? String(props.value) : '');

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    const handleInput = e => {
        emit('input', e.target.value);
    };

    const handleChange = e => {
        emit('change', e.target.value);
    };

    watch(() => props.value, newVal => {
        localValue.value = newVal != null ? String(newVal) : '';
    });
</script>

<script>
    export default {
        inheritAttrs: false
    };
</script>
